// src/pages/common-components/Auth/AuthShared.tsx
// Shared layout/styles for Login, Signup, and ForgotPassword — kept in one
// place so the three pages look and behave consistently.
import React from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../../Contexts/CompanyContext';

// ── Inline SVG icons (stroke-based, matches the icon style used on Contact) ──
const CarIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
    <rect x="3" y="11" width="18" height="6" rx="2" />
    <circle cx="7.5" cy="17.5" r="1.5" />
    <circle cx="16.5" cy="17.5" r="1.5" />
  </svg>
);

const ClipboardIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="12" height="17" rx="2" />
    <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
    <path d="M9 11h6M9 15h6M9 19h3" />
  </svg>
);

const BoltIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const AUTH_BENEFITS = [
  { Icon: CarIcon, text: 'Save your favorite cars and pick up your search anytime' },
  { Icon: ClipboardIcon, text: 'Track trade-ins, test drives, and reservation requests in one place' },
  { Icon: BoltIcon, text: 'Faster checkout — your details are ready when you reserve a car' },
];

export const authInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #ddd',
  borderRadius: 3,
  fontSize: 16,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

export const authLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#222',
  marginBottom: 6,
};

export const authSubmitStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 32px',
  background: '#717fe0',
  color: '#fff',
  border: 'none',
  borderRadius: 3,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.2s',
};

// Standalone page shell for Login/Signup/ForgotPassword — deliberately does
// NOT render the site Header/Footer. Real sign-in pages are their own
// self-contained experience (just a logo back to home + the form), not the
// full marketing site chrome, so this owns the whole viewport itself.
export const AuthCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => {
  const { companyProfile } = useCompany();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f7f7fb' }}>
      {/* Minimal top bar — logo on the left, explicit way back to the site on the right */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 24px' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          {companyProfile?.logo && (
            <img
              src={companyProfile.logo}
              alt={companyProfile.companyName}
              style={{ height: 32, objectFit: 'contain' }}
            />
          )}
          <span style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
            {companyProfile?.companyName || ''}
          </span>
        </Link>

        <Link
          to="/"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 100, border: '1px solid #ddd',
            color: '#444', fontSize: 13, fontWeight: 600, textDecoration: 'none',
            background: '#fff', transition: 'all 0.2s',
          }}
          className="auth-back-btn"
        >
          <ArrowLeftIcon />
          Back to Website
        </Link>
      </div>

      <div className="container" style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', padding: '20px 15px 60px' }}>
        <div
          style={{
            display: 'flex',
            maxWidth: 920,
            width: '100%',
            margin: '0 auto',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid #e5e5e5',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          {/* Branded panel — hidden on small screens */}
          <div
            className="auth-brand-panel"
            style={{
              flex: '0 0 42%',
              background: 'linear-gradient(150deg, #717fe0 0%, #4f5bc0 100%)',
              color: '#fff',
              padding: '48px 36px',
              display: 'none',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {companyProfile?.logo && (
              <img
                src={companyProfile.logo}
                alt={companyProfile.companyName}
                style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, background: '#fff', padding: 4, marginBottom: 22 }}
              />
            )}
            <h3 style={{ color: '#fff', marginBottom: 14, fontSize: 24 }}>
              {companyProfile?.companyName || 'Find your next car'}
            </h3>
            <p style={{ opacity: 0.9, fontSize: 14, lineHeight: 1.7, marginBottom: 30 }}>
              Sign in to manage your test drives, trade-ins, and saved vehicles — all from one account.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {AUTH_BENEFITS.map(({ Icon, text }) => (
                <div key={text} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon />
                  </span>
                  <span style={{ fontSize: 13, opacity: 0.92, lineHeight: 1.6, paddingTop: 6 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form panel */}
          <div style={{ flex: '1 1 auto', padding: '40px 36px', background: '#fff', minWidth: 0 }}>
            <h4 style={{ marginBottom: subtitle ? 6 : 24, textAlign: 'center' }}>{title}</h4>
            {subtitle && (
              <p style={{ margin: '0 0 24px', textAlign: 'center', color: '#888', fontSize: 14 }}>{subtitle}</p>
            )}
            {children}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .auth-brand-panel { display: flex !important; }
        }
        .auth-back-btn:hover {
          border-color: #717fe0;
          color: #717fe0;
        }
      `}</style>
    </div>
  );
};

export const AuthError: React.FC<{ message: string }> = ({ message }) =>
  !message ? null : (
    <div
      style={{
        background: '#fdecea',
        border: '1px solid #f5c6cb',
        color: '#c62828',
        borderRadius: 3,
        padding: '10px 12px',
        fontSize: 13,
        marginBottom: 18,
      }}
    >
      {message}
    </div>
  );

export const AuthSuccess: React.FC<{ message: string }> = ({ message }) =>
  !message ? null : (
    <div
      style={{
        background: '#eaf6ea',
        border: '1px solid #b7e1b7',
        color: '#2e7d32',
        borderRadius: 3,
        padding: '10px 12px',
        fontSize: 13,
        marginBottom: 18,
      }}
    >
      {message}
    </div>
  );

// Maps common Firebase Auth error codes to friendly copy.
export const friendlyAuthError = (err: any): string => {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
};
